import React, { useEffect, useRef, useState } from "react";
import { X } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { OrderFeedbackForm } from "@/components/OrderFeedbackForm";

const FEEDBACK_PROMPT_POLL_INTERVAL_MS = 30 * 1000;

export const PostLoginFeedbackPrompt = () => {
  const { user, checking } = useAuth();
  const [feedback, setFeedback] = useState(null);
  const feedbackRef = useRef(null);
  const promptLoaderRef = useRef(() => {});
  const dismissalInFlightRef = useRef(false);

  useEffect(() => {
    feedbackRef.current = feedback;
  }, [feedback]);

  useEffect(() => {
    if (checking) return undefined;
    if (!user) {
      feedbackRef.current = null;
      setFeedback(null);
      return undefined;
    }
    let active = true;
    let checkingPrompt = false;

    const loadPrompt = async () => {
      if (
        !active
        || checkingPrompt
        || feedbackRef.current
        || dismissalInFlightRef.current
      ) {
        return;
      }
      if (document.visibilityState === "hidden") {
        return;
      }
      checkingPrompt = true;
      try {
        const response = await api.get("/me/feedback/login-prompt");
        if (!active || !response.data.eligible) {
          return;
        }
        feedbackRef.current = response.data;
        setFeedback(response.data);
      } catch {
        if (active) {
          feedbackRef.current = null;
          setFeedback(null);
        }
      } finally {
        checkingPrompt = false;
      }
    };

    promptLoaderRef.current = loadPrompt;
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        loadPrompt();
      }
    };

    loadPrompt();
    const intervalId = window.setInterval(
      loadPrompt,
      FEEDBACK_PROMPT_POLL_INTERVAL_MS,
    );
    window.addEventListener("focus", loadPrompt);
    document.addEventListener("visibilitychange", handleVisibilityChange);
    window.addEventListener("pageshow", loadPrompt);

    return () => {
      active = false;
      window.clearInterval(intervalId);
      window.removeEventListener("focus", loadPrompt);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("pageshow", loadPrompt);
      promptLoaderRef.current = () => {};
    };
  }, [checking, user]);

  const dismissPrompt = async () => {
    const dismissedFeedback = feedback;
    feedbackRef.current = null;
    setFeedback(null);
    if (!dismissedFeedback?.order_id) {
      return;
    }
    dismissalInFlightRef.current = true;
    try {
      await api.post(
        `/me/feedback/login-prompt/${dismissedFeedback.order_id}/dismiss`,
      );
      dismissalInFlightRef.current = false;
      promptLoaderRef.current();
    } catch {
      dismissalInFlightRef.current = false;
      feedbackRef.current = dismissedFeedback;
      setFeedback(dismissedFeedback);
      toast.error("Could not save your feedback preference. Please try again.");
    }
  };

  const closeAfterSubmission = () => {
    feedbackRef.current = null;
    setFeedback(null);
    promptLoaderRef.current();
  };

  if (!feedback) {
    return null;
  }

  return (
    <div
      className={[
        "fixed inset-0 z-[90] flex items-end bg-[#1E2A24]/45 p-4",
        "sm:items-center sm:justify-center",
      ].join(" ")}
      data-testid="post-login-feedback-dialog"
      role="dialog"
      aria-modal="true"
      aria-label="Rate your delivered order"
    >
      <div
        className={[
          "max-h-[88vh] w-full max-w-2xl overflow-y-auto border border-[#E2DCD2]",
          "bg-[#FDFBF7] p-5 shadow-2xl sm:p-7",
        ].join(" ")}
      >
        <div className="mb-5 flex items-start justify-between gap-4">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.16em] text-[#8C6D46]">
              Delivered order
            </p>
            <h2 className="mt-2 font-serif text-3xl font-semibold text-[#1E2A24]">
              How was your order?
            </h2>
            <p className="mt-2 text-sm text-[#64748B]">
              Rate each product to earn Eira Points.
            </p>
          </div>
          <button
            type="button"
            onClick={dismissPrompt}
            className={[
              "rounded border border-[#E2DCD2] p-2 text-[#64748B]",
              "hover:border-[#C86D42] hover:text-[#C86D42]",
            ].join(" ")}
            aria-label="Close feedback prompt"
            data-testid="dismiss-post-login-feedback"
          >
            <X size={16} />
          </button>
        </div>
        <OrderFeedbackForm
          feedback={feedback}
          testIdPrefix="post-login"
          onSubmitted={closeAfterSubmission}
        />
      </div>
    </div>
  );
};